import { create } from 'zustand'
import type { User } from '@/types/api'
import apiClient, { SessionRecoveryUnavailableError } from '@/lib/api'
import billingApi from '@/lib/billingApi'
import { listServices } from '@/lib/servicesApi'
import {
  isPendingVerificationResponse,
  PendingVerificationRequired,
  pendingVerificationSession,
} from '@/lib/pendingVerificationSession'
import { clearRegistrationSessionStartup } from '@/lib/registrationSessionStartup'
import authSession from '@/lib/authSession'
// Removed latency warmups to avoid background pings except on location page

interface AuthState {
  user: User | null
  isLoading: boolean
  isAuthenticated: boolean
  forcePasswordChange: boolean
  requiresPaymentMethod: boolean
  packageCardOptional: boolean
  requiresEmailVerification: boolean
  authUnavailable: boolean
  login: (username: string, password: string) => Promise<void>
  logout: () => Promise<void>
  initializeAuth: () => Promise<void>
  setUser: (user: User | null) => void
  updateUser: (userUpdate: Partial<User>) => void
  ensureUuid: () => Promise<void>
  setForcePasswordChange: (force: boolean) => void
  clearForcePasswordChange: () => void
  setRequiresPaymentMethod: (requires: boolean) => void
  clearRequiresPaymentMethod: () => void
  refreshPaymentMethodGate: () => Promise<boolean>
  setRequiresEmailVerification: (requires: boolean) => void
  clearRequiresEmailVerification: () => void
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  isLoading: true,
  isAuthenticated: false,
  forcePasswordChange: false,
  requiresPaymentMethod: false,
  packageCardOptional: false,
  requiresEmailVerification: false,
  authUnavailable: false,
  // simple in-memory cooldown for initializeAuth 401s
  ...(typeof window !== 'undefined' ? (() => {
    let lastInitFailAt = 0
    ;(window as any).__peltast_setInitFailTime = () => { lastInitFailAt = Date.now() }
    ;(window as any).__peltast_canInitAuth = () => (Date.now() - lastInitFailAt) > 3000
    return {}
  })() : {}),

  setUser: (user: User | null) => set({ user, isAuthenticated: !!user }),
  setForcePasswordChange: (force: boolean) => set({ forcePasswordChange: force }),
  clearForcePasswordChange: () => set({ forcePasswordChange: false }),
  setRequiresPaymentMethod: (requires: boolean) => set({ requiresPaymentMethod: requires }),
  clearRequiresPaymentMethod: () => set({ requiresPaymentMethod: false, packageCardOptional: false }),
  refreshPaymentMethodGate: async () => {
    const account = get().user
    if (!account || !get().isAuthenticated) return false
    try {
      // Hoard's login gate checks whether this Stripe customer has a card on file.
      // Its owner-scoped billing endpoint lists the same attached card methods.
      const methods = await apiClient.getPaymentMethods()
      if (!Array.isArray(methods) || !methods.some(method => typeof method?.id === 'string' && method.id.length > 0)) return false
      const current = get()
      const sameAccount = Boolean(account.uuid && current.user?.uuid === account.uuid)
        || Boolean(account.id && current.user?.id === account.id)
      if (!current.isAuthenticated || !sameAccount) return false
      set({ requiresPaymentMethod: false, packageCardOptional: false })
      return true
    } catch {
      return false
    }
  },
  setRequiresEmailVerification: (requires: boolean) => set({ requiresEmailVerification: requires }),
  clearRequiresEmailVerification: () => set({ requiresEmailVerification: false }),
  // Register a global unauthorized listener once
  ...(typeof window !== 'undefined' ? (() => {
    const onUnauthorized = () => {
      clearRegistrationSessionStartup()
      authSession.clearCredentials()
      apiClient.clearPanelCredentials()
      set({ user: null, isAuthenticated: false, forcePasswordChange: false, requiresPaymentMethod: false, packageCardOptional: false, requiresEmailVerification: false, authUnavailable: false })
    }
    try { window.addEventListener('peltast:unauthorized', onUnauthorized) } catch {}
    return {}
  })() : {}),

  login: async (username: string, password: string) => {
    set({ isLoading: true, authUnavailable: false })
    let platformRole: User['role'] | undefined

    try {
      // Perform session-based login; if user payload is returned, use it
      const loginResp = await apiClient.login({ username, password }) as (import('@/types/api').AuthResponse & { user?: import('@/types/api').User; force_password_change?: boolean; requires_payment_method?: boolean; requires_email_verification?: boolean })
      if (isPendingVerificationResponse(loginResp)) {
        apiClient.clearPanelCredentials()
        pendingVerificationSession.capture(loginResp)
        set({ user: null, isAuthenticated: false, requiresEmailVerification: true })
        throw new PendingVerificationRequired()
      }
      if (loginResp && (loginResp as any).user) {
        platformRole = (loginResp as any).user.role
        set({ 
          user: (loginResp as any).user, 
          isAuthenticated: true,
          forcePasswordChange: !!(loginResp as any).force_password_change,
          requiresPaymentMethod: !!(loginResp as any).requires_payment_method,
          packageCardOptional: !!loginResp.package_card_optional,
          requiresEmailVerification: !!(loginResp as any).requires_email_verification,
        })
        // Immediately hydrate full profile to get first/last name and uuid
        try {
          const full = await apiClient.getAccount()
          if (full) { platformRole = full.role; set({ user: full, isAuthenticated: true }) }
        } catch {}
      } else {
        const user = await apiClient.initializeAuth()
        if (!user) throw new Error('Login failed')
        platformRole = user.role
        set({ user, isAuthenticated: true })
      }

      // Acquire Hoard JWT for billing/subscription calls (fixes 401 on provision)
      try {
        const hoardResult = loginResp.access_token
          ? loginResp
          : await billingApi.loginWithCredentials(username, password)
        if (isPendingVerificationResponse(hoardResult)) {
          apiClient.clearPanelCredentials()
          pendingVerificationSession.capture(hoardResult)
          set({ user: null, isAuthenticated: false, requiresEmailVerification: true })
          throw new PendingVerificationRequired()
        }
        // Check for force_password_change in Hoard response
        if (hoardResult?.force_password_change) {
          set({ forcePasswordChange: true })
        }
        if (hoardResult?.requires_payment_method) {
          set({ requiresPaymentMethod: true })
        }
        set({ packageCardOptional: !!hoardResult?.package_card_optional })
        if (typeof hoardResult?.requires_email_verification !== 'undefined') {
          set({ requiresEmailVerification: !!hoardResult.requires_email_verification })
        }
      } catch (error) {
        if (error instanceof PendingVerificationRequired) throw error
      }

      // Background prefetch to make UX snappier post-login
      if (platformRole !== 'sre') {
        try { ;(async () => { try { await listServices() } catch {} })() } catch {}
      }
    } catch (error) {
      set({
        user: null,
        isAuthenticated: false,
        forcePasswordChange: false,
        requiresPaymentMethod: false,
        packageCardOptional: false,
        requiresEmailVerification: error instanceof PendingVerificationRequired,
      })
      throw error
    } finally {
      set({ isLoading: false })
    }
  },

  logout: async () => {
    set({ isLoading: true, authUnavailable: false })
    
    try {
      // Revoke Hoard refresh token before clearing state
      try {
        const refreshToken = typeof window !== 'undefined' ? localStorage.getItem('refresh_token') : null
        if (refreshToken) {
          await fetch('/hoard-auth/token/revoke', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refresh_token: refreshToken }),
          })
        }
      } catch {}

      await apiClient.logout()
  authSession.clearCredentials()
  try { sessionStorage.removeItem('force_password_change') } catch {}
  try { pendingVerificationSession.clear() } catch {}
  try { clearRegistrationSessionStartup() } catch {}
  try { localStorage.removeItem('peltast:logout') } catch {}
      set({ user: null, isAuthenticated: false, forcePasswordChange: false, requiresPaymentMethod: false, packageCardOptional: false, requiresEmailVerification: false, authUnavailable: false })
    } finally {
      set({ isLoading: false })
    }
  },

  initializeAuth: async () => {
  // throttle retries if we recently failed; avoids spinner loops on flaky nets
    const canRun = typeof window === 'undefined' ? true : (window as any).__peltast_canInitAuth?.() ?? true
    if (!canRun) { set({ isLoading: false }); return }
    set({ isLoading: true, authUnavailable: false })
    
    try {
  const user = await apiClient.initializeAuth()
      
  if (user) {
        set({ 
          user, 
          isAuthenticated: true, 
          isLoading: false,
          authUnavailable: false,
        })

        // If we already have a user session (e.g., remember-me), attempt to
        // ensure Hoard JWT exists using a lightweight heuristic. If missing,
        // we can't auto-login without a password, so defer until explicit login.
        try {
          const hasToken = !!authSession.getAccessToken()
          if (hasToken) {
            try {
              const verifyResp = await billingApi.get('/hoard-auth/email-verification/status')
              set({ requiresEmailVerification: !!verifyResp?.data?.required })
            } catch {}
          } else {
            set({ requiresEmailVerification: false })
          }

          if (user.role !== 'sre') {
            ;(async () => { try { await listServices() } catch {} })()
          }
        } catch {}
      } else {
        set({ 
          user: null, 
          isAuthenticated: false, 
          requiresEmailVerification: false,
          isLoading: false,
          authUnavailable: false,
        })
      }
    } catch (error) {
  if (error instanceof SessionRecoveryUnavailableError) {
    set({ isLoading: false, authUnavailable: true })
    return
  }
  set({ 
        user: null, 
        isAuthenticated: false, 
        requiresEmailVerification: false,
        authUnavailable: false,
        isLoading: false 
      })
  try { (window as any).__peltast_setInitFailTime?.() } catch {}
    }
  },

  updateUser: (userUpdate: Partial<User>) => {
    set((state) => ({
      user: state.user ? { ...state.user, ...userUpdate } : null
    }))
  },
  ensureUuid: async () => {
    const state: any = useAuthStore.getState()
    const u: any = state.user
    if (!u || u.uuid) return
    try {
      const id = u.id
      // Use Hoard subscription account enrichment endpoint
      const resp = await billingApi.get(`/subscription/account/${id}`)
      const data = resp?.data?.data
      if (data?.uuid) {
        set({ user: { ...u, uuid: data.uuid } })
      }
    } catch {}
  },
}))
